import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MonitorHead, StatusBadge } from '../../components';
import { PipelineNavIcon } from '../../components/navIcons';
import { apiService } from '../../services';
import type { ScoringRunsData } from '../../types';
import { formatMonitorStamp, formatRunStarted, formatUpdatedAt } from '../../utils/monitorFormat';
import {
  hubSyncSummary,
  runStatusLabel,
  runStatusTone,
  stageLabel,
  waitingAccent,
} from '../../utils/monitorStatus';
import '../monitoring/monitoring.css';

const STAGE_KEYS = ['summarizing', 'classifying', 'scoring', 'finalizing'] as const;

export const Pipeline: React.FC = () => {
  const [data, setData] = useState<ScoringRunsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getScoringRuns(30)
      .then((res) => {
        if (cancelled) return;
        if (res.success) setData(res.data);
        else setError(res.message || 'Failed to load pipeline');
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load pipeline');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const latest = data?.runs[0];
  const scoringTone = runStatusTone(latest?.status);
  const hub = hubSyncSummary(data?.hub_sync);
  const waiting = data?.waiting_count ?? 0;
  const updatedIso =
    latest?.last_heartbeat_at || latest?.started_at || data?.hub_sync.last_run_at || null;

  return (
    <div className="monitor-page">
      <main className="monitor-page__content">
        <MonitorHead
          title="Pipeline"
          icon={<PipelineNavIcon size={20} />}
          subtitle="Whether prompts are being scored and whether the Hub sync ran. Run logs, so no time-window switch."
          aside={updatedIso ? <span className="monitor-updated">{formatUpdatedAt(updatedIso)}</span> : undefined}
        />

        {error && <p className="monitor-error">{error}</p>}

        <div className="monitor-kpis">
          <div className={`monitor-kpi monitor-kpi--${waitingAccent(waiting)}`}>
            <span className="monitor-kpi__eyebrow">Waiting to be scored</span>
            <span className="monitor-kpi__value num">{loading ? '—' : waiting}</span>
            <span className="monitor-kpi__meta">unscored, not parked for LLM escalation</span>
          </div>
          <div className={`monitor-kpi monitor-kpi--${scoringTone}`}>
            <span className="monitor-kpi__eyebrow">Latest scoring run</span>
            {latest ? (
              <>
                <span className="monitor-kpi__row">
                  <StatusBadge tone={scoringTone}>{runStatusLabel(latest.status)}</StatusBadge>
                  <span className="monitor-kpi__meta">
                    in {stageLabel(latest.current_stage)} · {formatRunStarted(latest.started_at)}
                  </span>
                </span>
                <Link className="monitor-row-link" to={`/pipeline/runs/${latest.id}`}>
                  Open this run →
                </Link>
              </>
            ) : (
              <span className="monitor-kpi__meta">{loading ? 'Loading…' : 'No runs yet'}</span>
            )}
          </div>
          <div className={`monitor-kpi monitor-kpi--${hub.tone}`}>
            <span className="monitor-kpi__eyebrow">Latest Hub sync</span>
            <span className="monitor-kpi__row">
              <StatusBadge tone={hub.tone}>{hub.label}</StatusBadge>
              {data?.hub_sync.last_run_at && (
                <span className="monitor-kpi__meta">{formatRunStarted(data.hub_sync.last_run_at)}</span>
              )}
            </span>
            <Link className="monitor-row-link" to="/hub-sync-logs">
              Open Hub sync →
            </Link>
          </div>
        </div>

        <div className="monitor-card">
          <div className="monitor-card__head">
            <h2>Scoring runs</h2>
            <span className="monitor-legend">
              <span><span className="stg-dots"><i className="done" /></span>done</span>
              <span><span className="stg-dots"><i className="running" /></span>in progress</span>
              <span><span className="stg-dots"><i className="failed" /></span>failed here</span>
            </span>
          </div>
          {loading && !data && <p className="monitor-empty">Loading scoring runs…</p>}
          {!loading && data && data.runs.length === 0 && (
            <p className="monitor-empty">No scoring runs yet.</p>
          )}
          {data && data.runs.length > 0 && (
            <>
              <div className="monitor-table-wrap">
                <table className="monitor-table" style={{ minWidth: 1040 }}>
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Finished</th>
                      <th>Status</th>
                      <th>Stage</th>
                      <th>Summarize · Classify · Score · Finalize</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.runs.map((run) => {
                      const tone = runStatusTone(run.status);
                      return (
                        <tr key={run.id}>
                          <td className="nowrap">{formatMonitorStamp(run.started_at)}</td>
                          <td className="nowrap">{formatMonitorStamp(run.finished_at)}</td>
                          <td>
                            <StatusBadge tone={tone}>{runStatusLabel(run.status)}</StatusBadge>
                          </td>
                          <td>{stageLabel(run.current_stage)}</td>
                          <td>
                            <span className="stg-dots">
                              {STAGE_KEYS.map((key) => (
                                <i key={key} className={run.stages?.[key] || ''} />
                              ))}
                            </span>
                          </td>
                          <td className="r">
                            <Link className="monitor-row-link" to={`/pipeline/runs/${run.id}`}>
                              Open run
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="monitor-foot">
                Showing {data.runs.length} of the latest runs · newest first
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Pipeline;
